/**
 * EnergyControlIntakeHero — dark Powersure variant (Feb 2026 Phase 3).
 *
 * Maakt de intake minder zwaar voordat de klant aan vragen begint:
 *   - korte titel + uitleg
 *   - 4 promise-bullets ("ongeveer 2 minuten", "geen technische kennis nodig",
 *     "later kunt u apparaten koppelen", "u krijgt direct een eerste plan")
 *   - prijsblok (€ 9,95 → € 14,95)
 *   - trust-regel (geen leverancierswissel etc.)
 *   - primaire CTA naar eerste vraag
 *
 * Gebruikt Powersure UI primitives zodat de stijl consistent is met
 * landing en report page.
 */
import { ArrowRight, ShieldCheck, Clock, Sparkles, Zap } from 'lucide-react';
import PowersureCard from '@/components/powersure-ui/PowersureCard';
import PriceBlock from '@/components/powersure-ui/PriceBlock';
import { psCls } from '@/lib/powersure-theme';

const PROMISES = [
  { icon: Clock,         text: '±2 minuten' },
  { icon: Sparkles,      text: 'Geen technische kennis nodig' },
];

export default function EnergyControlIntakeHero() {
  const scrollToFirstQuestion = () => {
    if (typeof document === 'undefined') return;
    const target = document.querySelector('[data-testid="qs-step-1"]');
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section
      className="max-w-2xl mx-auto mb-6 hidden sm:block"
      data-testid="ec-intake-hero"
      aria-labelledby="ec-intake-hero-h1"
    >
      <PowersureCard padding="p-5 sm:p-7">
        {/* Brand mark + Ads-match tagline */}
        <div className="flex items-center gap-2 mb-2">
          <span className={psCls.iconBadge}>
            <Zap className="w-4 h-4" />
          </span>
          <span className={psCls.label}>Mijn Powersure</span>
        </div>
        <div
          className="mb-3 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-400/30 text-[11px] font-medium text-amber-300"
          data-testid="ec-intake-hero-saldering-tag"
        >
          Salderen stopt vanaf 2027
        </div>

        <h1
          id="ec-intake-hero-h1"
          className="text-2xl sm:text-3xl font-medium text-white leading-tight"
          style={{ fontFamily: 'Outfit' }}
          data-testid="ec-intake-hero-h1"
        >
          Gratis eerste inzicht in uw zonnestroomkansen
        </h1>
        <p
          className="mt-2 text-sm sm:text-[15px] text-white/75 leading-relaxed"
          data-testid="ec-intake-hero-sub"
        >
          Gebruik meer van uw eigen zonnestroom — drie korte vragen, daarna direct uw voorbeeld-overzicht.
        </p>

        {/* Promise-bullets — 4 redenen waarom dit makkelijk is */}
        <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="ec-intake-hero-promises">
          {PROMISES.map(({ icon: Icon, text }, idx) => (
            <li
              key={idx}
              className="flex items-start gap-2 rounded-lg bg-white/5 border border-white/10 px-3 py-2"
              data-testid={`ec-intake-hero-promise-${idx + 1}`}
            >
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 shrink-0">
                <Icon className="w-3.5 h-3.5" />
              </span>
              <span className="text-[13px] text-white/85 leading-snug pt-0.5">{text}</span>
            </li>
          ))}
        </ul>

        {/* Prijsblok */}
        <PriceBlock
          testId="ec-intake-hero-price"
          className="mt-4"
          paymentNote={null}
        />

        {/* Trust-regel */}
        <div
          className="mt-3 flex items-start gap-2 rounded-lg bg-white/5 border border-white/10 px-3 py-2.5"
          data-testid="ec-intake-hero-trust"
        >
          <ShieldCheck className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />
          <p className="text-[12.5px] text-white/75 leading-snug">
            Geen leverancierswissel · geen verkoper aan de deur · geen automatische sturing zonder uw OK
          </p>
        </div>

        {/* Primaire CTA */}
        <button
          type="button"
          onClick={scrollToFirstQuestion}
          className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-[15px] font-semibold transition-colors shadow-lg shadow-emerald-900/40"
          data-testid="ec-intake-hero-cta"
        >
          Start nu — gratis eerste inzicht
          <ArrowRight className="w-4 h-4" />
        </button>

        <p
          className="mt-2 text-[11px] text-white/45 leading-relaxed text-center"
          data-testid="ec-intake-hero-microcopy"
        >
          ±2 minuten · u kunt later altijd aanpassen
        </p>
      </PowersureCard>
    </section>
  );
}
